import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { inicioDoPapel, usePode, useSair, useUsuario, type Permissoes } from '../lib/sessao.tsx';
import { Caetech, Marca } from './Marca.tsx';
import { Tema } from './Tema.tsx';
import './layout.css';

interface Item {
  para: string;
  nome: string;
  permissoes: (keyof Permissoes)[];
  /** Mostra quantas pendências existem, direto no menu. */
  contador?: boolean;
}

/* A ordem segue o dia da recepcao, nao a ordem alfabetica nem a do banco:
   a fila e o que se olha o tempo todo, cadastros sao consulta eventual. */
const OPERACAO: Item[] = [
  { para: '/atendimento', nome: 'Novo atendimento', permissoes: ['pode_exame_cadastrar'] },
  { para: '/fila', nome: 'Fila de exames', permissoes: ['pode_exame_consultar', 'pode_exame_cadastrar'] },
  { para: '/quarentena', nome: 'Imagens sem exame', permissoes: ['pode_exame_consultar', 'pode_exame_cadastrar'], contador: true },
  { para: '/pacientes', nome: 'Pacientes', permissoes: ['pode_pacientes', 'pode_exame_cadastrar'] },
  { para: '/empresas', nome: 'Empresas', permissoes: ['pode_empresas'] },
  { para: '/retirar-laudos', nome: 'Laudos ocupacionais', permissoes: ['pode_ocupacional'] },
];

const ADMINISTRACAO: Item[] = [
  { para: '/inicio', nome: 'Painel', permissoes: ['pode_usuarios'] },
  { para: '/relatorios', nome: 'Relatórios', permissoes: ['pode_relatorios'] },
  { para: '/configuracoes', nome: 'Configurações', permissoes: ['pode_usuarios'] },
];

const CADASTROS: Item[] = [
  { para: '/procedimentos', nome: 'Procedimentos', permissoes: ['pode_procedimentos'] },
  { para: '/especialidades', nome: 'Especialidades', permissoes: ['pode_procedimentos'] },
  { para: '/convenios', nome: 'Convênios', permissoes: ['pode_convenios'] },
  { para: '/medicos', nome: 'Médicos', permissoes: ['pode_medicos'] },
  { para: '/usuarios', nome: 'Usuários', permissoes: ['pode_usuarios'] },
];

function Grupo({ titulo, itens }: { titulo: string; itens: Item[] }) {
  const pode = usePode();
  // Item sem permissões declaradas é do papel inteiro (o médico, por exemplo).
  const visiveis = itens.filter((i) => i.permissoes.length === 0 || pode(...i.permissoes));

  // Imagem parada sem exame é o problema que trava o atendimento. O número
  // aparece no menu para ninguém precisar abrir a tela para descobrir.
  const presas = useQuery({
    queryKey: ['quarentena'],
    queryFn: ({ signal }) => api.get<{ total: number }>('/quarentena', signal),
    enabled: visiveis.some((i) => i.contador),
    refetchInterval: 120_000,
    staleTime: 60_000,
  });

  if (visiveis.length === 0) return null;

  return (
    <div className="rail__grupo">
      <p className="rail__titulo rotulo">{titulo}</p>
      <ul>
        {visiveis.map((i) => {
          const n = i.contador ? (presas.data?.total ?? 0) : 0;
          return (
            <li key={i.para}>
              <NavLink to={i.para} className="rail__item">
                {i.nome}
                {n > 0 && <span className="rail__contador num">{n}</span>}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* Cada papel tem o próprio menu, e dentro do menu da clínica cada item depende
   da permissão de quem entrou — esconder o que não se aplica é mais honesto que
   mostrar itens que devolvem "sem permissão" ao clicar. */
const MEDICO: Item[] = [
  { para: '/laudar', nome: 'Exames para laudar', permissoes: [] },
  { para: '/meus-laudos', nome: 'Laudos assinados', permissoes: [] },
];

/* Médico que acompanha mas não lauda: o clínico ligado ao exame. Entra
   para acompanhar e retirar o que é dele -- oferecer a ele "exames para laudar"
   seria uma tela sempre vazia. */
const MEDICO_SOLICITANTE: Item[] = [
  { para: '/meus-pedidos', nome: 'Exames que acompanho', permissoes: [] },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const usuario = useUsuario();
  const sair = useSair();
  const local = useLocation();

  /* No celular o menu é uma gaveta. Antes ele virava uma faixa de links com
     rolagem lateral no topo, e o rodapé do menu -- usuário, tema, sair --
     sobrava logo abaixo dela, no alto da tela, onde ninguém procura por isso.
     Como gaveta, a coluna inteira vai para o lado e o rodapé volta a ser
     rodapé. */
  const [menu, setMenu] = useState(false);

  // Fecha ao navegar: sem isto a gaveta fica aberta por cima da tela que a
  // pessoa acabou de escolher.
  useEffect(() => setMenu(false), [local.pathname]);

  // Esc fecha, como qualquer camada que cobre a tela.
  useEffect(() => {
    if (!menu) return;
    const aoTeclar = (e: KeyboardEvent) => e.key === 'Escape' && setMenu(false);
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [menu]);

  const subtitulo =
    usuario.papel === 'medico' ? usuario.crm : 'Radiologia';
  const itensDoMedico = usuario.podeLaudar === false ? MEDICO_SOLICITANTE : MEDICO;
  const inicio = inicioDoPapel(usuario.papel, usuario.podeLaudar !== false, usuario.admin);

  return (
    <div className={`app ${menu ? 'app--menu' : ''}`}>
      <a className="pular" href="#conteudo">
        Pular para o conteúdo
      </a>

      {/* Só aparece no celular: o botão do menu e a marca, para a tela não
          começar sem dizer de quem é o sistema. */}
      <header className="barra">
        <button
          className="barra__menu"
          onClick={() => setMenu(true)}
          aria-label="Abrir menu"
          aria-expanded={menu}
          aria-controls="menu-lateral"
        >
          <span className="barra__risco" />
          <span className="barra__risco" />
          <span className="barra__risco" />
        </button>
        <Link className="barra__marca" to={inicio} aria-label="Ir para o início">
          <Marca tamanho="sm" subtitulo={subtitulo} />
        </Link>
      </header>

      {/* Fundo que escurece e fecha ao toque. Fora do menu de propósito: quem
          quer sair da gaveta toca em qualquer lugar, não procura um X. */}
      <button
        className="rail__fundo"
        onClick={() => setMenu(false)}
        aria-label="Fechar menu"
        tabIndex={menu ? 0 : -1}
      />

      <aside className="rail" id="menu-lateral">
        {/* A logo leva ao início de quem está logado -- o painel, para o
            administrador; a fila, para a recepção. É o gesto que todo mundo já
            tenta em qualquer site, e até agora não fazia nada. */}
        <Link className="rail__marca" to={inicio} aria-label="Ir para o início">
          <Marca tamanho="sm" subtitulo={subtitulo} />
        </Link>
        <nav className="rail__nav" aria-label="Seções">
          {usuario.papel === 'medico' ? (
            <Grupo titulo={usuario.podeLaudar === false ? 'Acompanhamento' : 'Laudos'} itens={itensDoMedico} />
          ) : (
            <>
              <Grupo titulo="Operação" itens={OPERACAO} />
              <Grupo titulo="Cadastros" itens={CADASTROS} />
              <Grupo titulo="Administração" itens={ADMINISTRACAO} />
            </>
          )}
        </nav>
        <div className="rail__rodape">
          <p className="rail__usuario" title={usuario.email}>
            {usuario.nome}
          </p>
          <Tema />
          <button className="rail__sair" onClick={() => void sair()}>
            Sair
          </button>
          <Caetech className="rail__caetech" />
        </div>
      </aside>

      <main className="conteudo" id="conteudo">
        {children}
      </main>
    </div>
  );
}

/** Cabecalho de pagina: titulo a esquerda, acoes a direita. */
export function Cabecalho({
  titulo,
  contagem,
  children,
}: {
  titulo: string;
  contagem?: string;
  children?: React.ReactNode;
}) {
  return (
    <header className="cabecalho">
      <div className="cabecalho__texto">
        <h1 className="cabecalho__titulo">{titulo}</h1>
        {contagem && <span className="cabecalho__contagem num">{contagem}</span>}
      </div>
      {children && <div className="cabecalho__acoes">{children}</div>}
    </header>
  );
}
